import { __decorate, __extends, __metadata, __spreadArray } from "tslib";
import NeoModel from '../Model/NeoModel';
import { Observable, Computed } from '../Model/Decorators';
import { SortOperation } from './SortOperation';
/** A DataView is a container of data with sorting and filtering capability. */
var DataViewBase = /** @class */ (function () {
    function DataViewBase(originalSource) {
        this.originalSource = originalSource;
        this.sortInfo = [];
        this.noSortSymbol = Symbol("DataView.NoSort");
        this.allowSort = true;
        this.performsRemoteSorting = false;
    }
    /**
     * Resets the sort properties. A new sort property can be provided.
     * If the provided sort property matches the first existing sort property, and sortAscending is undefined, then the current sort order will be reversed.
     */
    DataViewBase.prototype.resetSort = function (property, sortAscending) {
        this.clearNonSortItems();
        if (property) {
            this.sortInfo = [SortOperation.fromPrevious(this.sortInfo.length === 1 ? this.sortInfo[0] : null, property, sortAscending)];
        }
        else {
            this.sortInfo = [];
        }
    };
    /**
     * Adds a sort property.
     * If the property already exists, the sort direction of the property will be reversed.
     */
    DataViewBase.prototype.addSort = function (property, sortAscending) {
        this.clearNonSortItems();
        var existing = this.sortInfo.find(function (c) { return c.column === property; });
        if (existing) {
            existing.sortAscending = !existing.sortAscending;
            this.sortInfo = __spreadArray([], this.sortInfo, true);
        }
        else {
            this.sortInfo = __spreadArray(__spreadArray([], this.sortInfo, true), [new SortOperation(property, sortAscending !== null && sortAscending !== void 0 ? sortAscending : SortOperation.getDefaultSortAscending(property))], false);
        }
    };
    Object.defineProperty(DataViewBase.prototype, "rawItems", {
        /** Gets the items before filtering or sorting. */
        get: function () {
            return this.originalSource;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Adds an item to the datasource that is not sorted by the current sort criteria. The item will always be positioned at the end of the list.
     * The next time changeSort or addSort is called, the item will be sorted normally.
     */
    DataViewBase.prototype.addNonSortedItem = function () {
        var newItem = this.rawItems.createChild();
        newItem[this.noSortSymbol] = true;
        this.rawItems.push(newItem);
        return newItem;
    };
    DataViewBase.prototype.setItems = function (items) {
        this.originalSource = items;
    };
    DataViewBase.prototype.clearNonSortItems = function () {
        var _this = this;
        // @ts-ignore
        this.rawItems.filter(function (c) { return c[_this.noSortSymbol] !== undefined; }).forEach(function (c) { return delete c[_this.noSortSymbol]; });
    };
    DataViewBase.prototype.getFilteredItems = function () {
        if (this.rawItems.length && this.itemFilter) {
            return this.rawItems.filter(this.itemFilter);
        }
        else {
            return this.rawItems;
        }
    };
    Object.defineProperty(DataViewBase.prototype, "filteredItems", {
        get: function () {
            return this.getFilteredItems();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DataViewBase.prototype, "sortedItems", {
        get: function () {
            return this.getSortedItems();
        },
        enumerable: false,
        configurable: true
    });
    DataViewBase.prototype.getSortedItems = function () {
        var _this = this;
        var filtered = this.filteredItems;
        if (filtered.length === 0) {
            return [];
        }
        else {
            var sortInfo = this.sortInfo;
            if (sortInfo && sortInfo.length) {
                var sortFns_1 = [];
                // @ts-ignore
                var itemsToSort_1 = filtered.filter(function (c) { return c[_this.noSortSymbol] === undefined; });
                // @ts-ignore
                var endItems = filtered.filter(function (c) { return c[_this.noSortSymbol] !== undefined; });
                for (var _i = 0, sortInfo_1 = sortInfo; _i < sortInfo_1.length; _i++) {
                    var sortColumn = sortInfo_1[_i];
                    var valueFn = void 0;
                    var property = sortColumn.column;
                    if (sortColumn.column.hasSortValue) {
                        valueFn = DataViewBase.getSortValueSortFn(property.propertyName);
                    }
                    else {
                        valueFn = DataViewBase.getDefaultSortValueFn(property.propertyName);
                    }
                    sortFns_1.push({ property: property, valueFn: valueFn, direction: sortColumn.sortAscending ? "asc" : "desc" });
                }
                DataViewBase.unTrackedSort(function () {
                    for (var i = sortFns_1.length - 1; i >= 0; i--) {
                        itemsToSort_1 = itemsToSort_1.sortBy(sortFns_1[i].valueFn, sortFns_1[i].direction, sortFns_1[i].property.propertyType);
                    }
                });
                return __spreadArray(__spreadArray([], itemsToSort_1, true), endItems, true);
            }
            return filtered;
        }
    };
    DataViewBase.prototype.getItems = function () {
        return this.sortedItems;
    };
    DataViewBase.prototype.getMetaItem = function () {
        var items = this.rawItems;
        if (items.childObjectType) {
            return items.getMetaItem();
        }
        else if (items.length) {
            return items[0];
        }
        else {
            return null;
        }
    };
    DataViewBase.unTrackedSort = function (callback) {
        callback();
    };
    DataViewBase.getDefaultSortValueFn = function (propertyName) {
        return function (c) { return c[propertyName]; };
    };
    DataViewBase.getSortValueSortFn = function (propertyName) { return function (c) { return c.meta[propertyName].sortValue; }; };
    __decorate([
        Observable(),
        __metadata("design:type", Array)
    ], DataViewBase.prototype, "sortInfo", void 0);
    __decorate([
        Computed(),
        __metadata("design:type", Array),
        __metadata("design:paramtypes", [])
    ], DataViewBase.prototype, "filteredItems", null);
    __decorate([
        Computed(),
        __metadata("design:type", Array),
        __metadata("design:paramtypes", [])
    ], DataViewBase.prototype, "sortedItems", null);
    return DataViewBase;
}());
export { DataViewBase };
var DataView = /** @class */ (function (_super) {
    __extends(DataView, _super);
    function DataView() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    DataView = __decorate([
        NeoModel
    ], DataView);
    return DataView;
}(DataViewBase));
export { DataView };
